import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Calendar,
  ShoppingCart,
  Package,
  StickyNote,
  TrendingUp,
  TrendingDown,
  Clock,
  Euro,
  Star,
  Trash2,
  Pin,
  PinOff,
  Plus,
  ExternalLink,
  Award,
  AlertTriangle,
} from 'lucide-react'
import {
  customers,
  Customer360Profile,
  CustomerOrder,
  CustomerProduct,
  CustomerNote,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { useToast } from '@/components/ui/Toast'

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

function RFMBadge({ segment, description }: { segment: string | null; description: string | null }) {
  if (!segment) return null

  const getSegmentColor = () => {
    switch (segment) {
      case 'Champions (VIP)':
        return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
      case 'Clientes Leales':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
      case 'En Riesgo':
        return 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400'
      case 'Perdidos':
        return 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400'
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <span className={cn('px-3 py-1 text-sm font-medium rounded-full inline-flex items-center gap-1', getSegmentColor())}>
        <Star className="w-3 h-3" />
        {segment}
      </span>
      {description && (
        <span className="text-xs text-slate-500 dark:text-slate-400">{description}</span>
      )}
    </div>
  )
}

function StatItem({ label, value, icon: Icon, trend }: {
  label: string
  value: string | number
  icon: any
  trend?: 'up' | 'down' | null
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
      <div className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
        <Icon className="w-4 h-4 text-slate-600 dark:text-slate-400" />
      </div>
      <div className="flex-1">
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1">
          {value}
          {trend === 'up' && <TrendingUp className="w-3 h-3 text-success-500" />}
          {trend === 'down' && <TrendingDown className="w-3 h-3 text-danger-500" />}
        </p>
      </div>
    </div>
  )
}

function OrderRow({ order }: { order: CustomerOrder }) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
      <td className="px-4 py-3">
        <span className="font-mono text-sm text-primary-600 dark:text-primary-400">
          {order.reference}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {formatDate(order.date)}
      </td>
      <td className="px-4 py-3">
        <span
          className="px-2 py-1 text-xs font-medium rounded-full"
          style={{ backgroundColor: order.status_color + '20', color: order.status_color }}
        >
          {order.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {order.products_count} productos
      </td>
      <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-white text-right">
        {order.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
      </td>
    </tr>
  )
}

function ProductCard({ product }: { product: CustomerProduct }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
      <img
        src={product.image_url || '/placeholder-product.png'}
        alt={product.name}
        className="w-14 h-14 object-contain rounded-lg bg-slate-50"
        onError={(e) => {
          (e.target as HTMLImageElement).src = '/placeholder-product.png'
        }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
          {product.name}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
          {product.reference}
        </p>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
          <span>{product.times_bought}x comprado</span>
          <span>{product.total_quantity} uds</span>
          <span className="font-semibold text-slate-700 dark:text-slate-300">
            {product.total_spent.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
          </span>
        </div>
      </div>
    </div>
  )
}

function NoteCard({
  note,
  onDelete,
  onTogglePin,
  isDeleting,
}: {
  note: CustomerNote
  onDelete: () => void
  onTogglePin: () => void
  isDeleting: boolean
}) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div
      className={cn(
        'p-4 bg-white dark:bg-slate-800 rounded-lg border transition-all',
        note.pinned
          ? 'border-primary-300 dark:border-primary-600 shadow-sm'
          : 'border-slate-200 dark:border-slate-700'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
            {note.note_text}
          </p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
            <span>{note.created_by}</span>
            <span>-</span>
            <span>{formatDate(note.created_at)}</span>
            {note.pinned && (
              <span className="flex items-center gap-1 text-primary-600 dark:text-primary-400">
                <Pin className="w-3 h-3" />
                Fijada
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onTogglePin}
            className="p-1.5 text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            title={note.pinned ? 'Desfijar nota' : 'Fijar nota'}
          >
            {note.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
          </button>
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="p-1.5 text-slate-400 hover:text-danger-600 dark:hover:text-danger-400 transition-colors disabled:opacity-50"
            title="Eliminar nota"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

interface CustomerDetailModalProps {
  email: string
  onClose: () => void
}

export default function CustomerDetailModal({ email, onClose }: CustomerDetailModalProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'orders' | 'products' | 'notes'>('orders')
  const [newNote, setNewNote] = useState('')

  // Queries
  const profileQuery = useQuery({
    queryKey: ['customer-profile-360', email],
    queryFn: () => customers.getProfile360(email),
  })

  const ordersQuery = useQuery({
    queryKey: ['customer-orders', email],
    queryFn: () => customers.getCustomerOrders(email),
    enabled: activeTab === 'orders',
  })

  const productsQuery = useQuery({
    queryKey: ['customer-products', email],
    queryFn: () => customers.getCustomerProducts(email),
    enabled: activeTab === 'products',
  })

  const notesQuery = useQuery({
    queryKey: ['customer-notes', email],
    queryFn: () => customers.getCustomerNotes(email),
    enabled: activeTab === 'notes',
  })

  // Mutations
  const createNoteMutation = useMutation({
    mutationFn: (noteText: string) => customers.createCustomerNote(email, { note_text: noteText }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', email] })
      setNewNote('')
      toast.success('Nota creada')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: number) => customers.deleteCustomerNote(email, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', email] })
      toast.success('Nota eliminada')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const togglePinMutation = useMutation({
    mutationFn: (noteId: number) => customers.toggleNotePin(email, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', email] })
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const profile = profileQuery.data

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  const handleCreateNote = () => {
    if (newNote.trim()) {
      createNoteMutation.mutate(newNote.trim())
    }
  }

  const tabs = [
    { id: 'orders' as const, label: 'Pedidos', icon: ShoppingCart, count: profile?.total_orders },
    { id: 'products' as const, label: 'Productos', icon: Package },
    { id: 'notes' as const, label: 'Notas', icon: StickyNote, count: notesQuery.data?.total },
  ]

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="absolute inset-4 lg:inset-8 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between p-4 lg:p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                {profile?.firstname?.[0] || '?'}
                {profile?.lastname?.[0] || ''}
              </div>
              <div>
                <h2 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-white">
                  {profile?.firstname} {profile?.lastname}
                </h2>
                <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {email}
                  </span>
                  {profile?.company && (
                    <span className="flex items-center gap-1">
                      <Building className="w-4 h-4" />
                      {profile.company}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={cn(
                      'px-2 py-0.5 text-xs font-medium rounded-full',
                      profile?.active
                        ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    )}
                  >
                    {profile?.active ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                    {profile?.group_name}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={`https://www.jvs-informatica.com/es/admin-dev/index.php?controller=AdminCustomers&id_customer=${profile?.id}&viewcustomer`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver en PrestaShop
              </a>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar - Profile Info */}
          <div className="w-80 flex-shrink-0 border-r border-slate-200 dark:border-slate-700 overflow-y-auto p-4 lg:p-6 space-y-6">
            {/* RFM Badge */}
            {profile?.rfm_segment && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Segmento RFM
                </h3>
                <RFMBadge segment={profile.rfm_segment} description={profile.rfm_segment_description ?? null} />
              </div>
            )}

            {/* Stats Grid */}
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Estadísticas
              </h3>
              <StatItem
                label="Total Gastado"
                value={profile?.total_spent?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) || '0 €'}
                icon={Euro}
              />
              <StatItem
                label="Pedidos Totales"
                value={profile?.total_orders || 0}
                icon={ShoppingCart}
              />
              <StatItem
                label="Ticket Medio"
                value={profile?.average_order?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) || '0 €'}
                icon={TrendingUp}
              />
              <StatItem
                label="Último Pedido"
                value={profile?.days_since_last_order ? `Hace ${profile.days_since_last_order} días` : 'N/A'}
                icon={Clock}
                trend={profile?.days_since_last_order && profile.days_since_last_order > 90 ? 'down' : null}
              />
              <StatItem
                label="Cliente Desde"
                value={formatDate(profile?.date_add || null)}
                icon={Calendar}
              />
            </div>

            {/* RFM Metrics */}
            {(profile?.recency_days || profile?.frequency || profile?.monetary_total) && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Métricas RFM
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {profile.recency_days || 0}
                    </p>
                    <p className="text-xs text-slate-500">R (días)</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {profile.frequency || 0}
                    </p>
                    <p className="text-xs text-slate-500">F (pedidos)</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {profile.monetary_total?.toLocaleString('es-ES', { maximumFractionDigits: 0 }) || 0}€
                    </p>
                    <p className="text-xs text-slate-500">M (valor)</p>
                  </div>
                </div>
                {profile.spending_trend !== null && profile.spending_trend !== undefined && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500">Tendencia:</span>
                    <span
                      className={cn(
                        'flex items-center gap-1 font-medium',
                        profile.spending_trend > 0
                          ? 'text-success-600'
                          : profile.spending_trend < 0
                          ? 'text-danger-600'
                          : 'text-slate-600'
                      )}
                    >
                      {profile.spending_trend > 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : profile.spending_trend < 0 ? (
                        <TrendingDown className="w-4 h-4" />
                      ) : null}
                      {(profile.spending_trend * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Addresses */}
            {profile?.addresses && profile.addresses.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Direcciones ({profile.addresses.length})
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {profile.addresses.map((addr) => (
                    <div
                      key={addr.id}
                      className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg text-sm"
                    >
                      <p className="font-medium text-slate-900 dark:text-white mb-1">{addr.alias}</p>
                      <p className="text-slate-600 dark:text-slate-400">{addr.address1}</p>
                      {addr.address2 && <p className="text-slate-600 dark:text-slate-400">{addr.address2}</p>}
                      <p className="text-slate-600 dark:text-slate-400">
                        {addr.postcode} {addr.city}, {addr.country}
                      </p>
                      {(addr.phone || addr.phone_mobile) && (
                        <p className="text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {addr.phone_mobile || addr.phone}
                        </p>
                      )}
                      {addr.vat_number && (
                        <p className="text-slate-500 dark:text-slate-400 mt-1">
                          VAT: {addr.vat_number}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content - Tabs */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Tab Navigation */}
            <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700 px-4 lg:px-6">
              <nav className="-mb-px flex space-x-6">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  const isActive = activeTab === tab.id

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                        isActive
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                      )}
                    >
                      <Icon
                        className={cn(
                          '-ml-0.5 mr-2 h-5 w-5',
                          isActive ? 'text-primary-500' : 'text-slate-400 group-hover:text-slate-500'
                        )}
                      />
                      <span>{tab.label}</span>
                      {tab.count !== undefined && (
                        <span
                          className={cn(
                            'ml-2 px-2 py-0.5 rounded-full text-xs',
                            isActive
                              ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                          )}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  )
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div>
                  {ordersQuery.isLoading ? (
                    <div className="text-center py-8 text-slate-500">Cargando pedidos...</div>
                  ) : ordersQuery.data?.orders.length === 0 ? (
                    <div className="text-center py-8">
                      <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">Este cliente no tiene pedidos</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-800">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                              Referencia
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                              Fecha
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                              Estado
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                              Productos
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                          {ordersQuery.data?.orders.map((order) => (
                            <OrderRow key={order.id} order={order} />
                          ))}
                        </tbody>
                      </table>
                      {ordersQuery.data && (
                        <div className="mt-4 text-sm text-slate-500 text-center">
                          Mostrando {ordersQuery.data.orders.length} de {ordersQuery.data.total} pedidos
                          - Total: {ordersQuery.data.total_spent.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Products Tab */}
              {activeTab === 'products' && (
                <div>
                  {productsQuery.isLoading ? (
                    <div className="text-center py-8 text-slate-500">Cargando productos...</div>
                  ) : productsQuery.data?.products.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">Este cliente no ha comprado productos</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {productsQuery.data?.products.map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-4">
                  {/* Add Note Form */}
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Escribe una nota sobre este cliente..."
                      className="input w-full min-h-[100px] resize-none"
                    />
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={handleCreateNote}
                        disabled={!newNote.trim() || createNoteMutation.isPending}
                        className="btn btn-primary"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {createNoteMutation.isPending ? 'Guardando...' : 'Añadir Nota'}
                      </button>
                    </div>
                  </div>

                  {/* Notes List */}
                  {notesQuery.isLoading ? (
                    <div className="text-center py-8 text-slate-500">Cargando notas...</div>
                  ) : notesQuery.data?.notes.length === 0 ? (
                    <div className="text-center py-8">
                      <StickyNote className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">No hay notas para este cliente</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notesQuery.data?.notes.map((note) => (
                        <NoteCard
                          key={note.id}
                          note={note}
                          onDelete={() => deleteNoteMutation.mutate(note.id)}
                          onTogglePin={() => togglePinMutation.mutate(note.id)}
                          isDeleting={deleteNoteMutation.isPending}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
